import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";

const FIELDS = [
  ["abn", "ABN", "abn"], ["address", "Address", "address"], ["phone", "Phone", "phone"], ["email", "Email", "contact_email"],
];

// A depot's own business details for its PDFs; blank fields use the company's.
export const DepotDetailsDialog = ({ depot, onClose, onSaved }) => {
  const { company } = useCompany();
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(depot); // keeps the name while the dialog fades out
  useEffect(() => { if (depot) { setShown(depot); setV(Object.fromEntries(FIELDS.map(([k]) => [k, depot[k] || ""]))); } }, [depot]);

  const save = async () => {
    setBusy(true);
    try { await api.depotDetails(shown.id, v); toast.success(`${shown.name} details saved`); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!depot} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="depot-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{shown?.name} business details</DialogTitle>
          <DialogDescription>Printed at the top of this depot&apos;s PDFs. Leave a field blank to use {company?.name || "the company"}&apos;s.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {FIELDS.map(([k, label, ck]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{label}</Label>
              <Input value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} placeholder={company?.[ck] || `Company ${label.toLowerCase()}`}
                maxLength={k === "address" ? 200 : k === "email" ? 200 : 20} data-testid={`depot-details-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="depot-details-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
